import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export type AppCreditField = {
  key: string;
  value: number;
  min?: number;
  readOnly?: boolean;
};

export type AppCreditEditorRow = {
  id: string;
  name: string;
  subtitle?: string;
  fields: AppCreditField[];
};

type AppCreditsEditorProps = {
  rows: AppCreditEditorRow[];
  fieldLabels: Record<string, string>;
  onChange: (rowId: string, fieldKey: string, value: number) => void;
  emptyText?: string;
};

const parseNumberInput = (raw: string, fallback: number): number => {
  if (raw.trim() === "") return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const AppCreditsEditor = ({
  rows,
  fieldLabels,
  onChange,
  emptyText = "אין אפליקציות",
}: AppCreditsEditorProps) => {
  const fieldKeys = Object.keys(fieldLabels);

  if (rows.length === 0) {
    return <div className="text-sm text-muted-foreground text-center py-6">{emptyText}</div>;
  }

  return (
    <Table className="text-right">
      <TableHeader>
        <TableRow>
          <TableHead className="text-right">אפליקציה</TableHead>
          {fieldKeys.map((key) => (
            <TableHead key={key} className="text-right">
              {fieldLabels[key]}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="text-right">
              <div className="font-medium">{row.name}</div>
              {row.subtitle ? (
                <div className="text-xs text-muted-foreground font-mono">{row.subtitle}</div>
              ) : null}
            </TableCell>
            {fieldKeys.map((key) => {
              const field = row.fields.find((item) => item.key === key);
              const value = field?.value ?? 0;
              return (
                <TableCell key={key} className="text-right">
                  <Input
                    type="number"
                    min={field?.min ?? 0}
                    className="w-28 ml-auto text-right font-mono"
                    value={Number.isFinite(value) ? value : 0}
                    disabled={field?.readOnly}
                    readOnly={field?.readOnly}
                    onChange={(event) => {
                      if (field?.readOnly) return;
                      onChange(row.id, key, parseNumberInput(event.target.value, 0));
                    }}
                  />
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default AppCreditsEditor;
